import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

declare const FB: any;

/**
 * Loads the Facebook JS SDK exactly once and exposes a promise-based `login()`
 * that returns the short-lived user access token from FB.login(). That token is
 * sent to the backend, which verifies it against the Facebook Graph API before
 * trusting it (see auth.service.ts -> loginWithFacebook on the backend).
 */
@Injectable({
  providedIn: 'root',
})
export class FacebookAuthService {
  private platformId = inject(PLATFORM_ID);
  private sdkReady: Promise<void> | null = null;

  private loadSdk(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.sdkReady) {
      return this.sdkReady;
    }

    this.sdkReady = new Promise<void>((resolve) => {
      if (typeof (window as any).FB !== 'undefined') {
        resolve();
        return;
      }

      (window as any).fbAsyncInit = () => {
        FB.init({
          appId: environment.facebookAppId,
          cookie: true,
          xfbml: false,
          version: 'v21.0',
        });
        resolve();
      };

      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    });

    return this.sdkReady;
  }

  async login(): Promise<string> {
    await this.loadSdk();

    return new Promise<string>((resolve, reject) => {
      FB.login(
        (response: any) => {
          const accessToken = response?.authResponse?.accessToken;
          if (accessToken) {
            resolve(accessToken);
          } else {
            reject(new Error('cancelled'));
          }
        },
        { scope: 'public_profile,email' }
      );
    });
  }
}
